import { useMemo } from 'react';
import { SideModal } from '@design-system/components/modal/sideModal';
import { Typography } from '@design-system/components/typography/typography';
import { Async } from '@client/new/helpers/types';
import { Community, CommunityId } from '../model/community.types';
import { postKey, userKey } from '../model/moderation.types';
import { useCommunityModeration } from '../api/useCommunityModeration';
import { BlockedUserCard } from './BlockedUserCard';
import { ModerationToggle } from './ModerationToggle';
import { ReportedPostCard } from './ReportedPostCard';

type Props = {
  communities: Community[];
  isOpen: boolean;
  onClose: () => void;
};

const Message = ({ text }: { text: string }) => (
  <Typography variant="body" size="medium" className="py-8 text-[#9E9E9E]">
    {text}
  </Typography>
);

const renderList = <T,>(
  state: Async<T[]>,
  emptyText: string,
  renderItems: (items: T[]) => JSX.Element,
): JSX.Element => {
  switch (state.type) {
    case 'not-fetched':
    case 'fetching':
      return <Message text="Loading…" />;
    case 'fetch-error':
      return <Message text="Couldn't load moderation data." />;
    case 'fetched':
      return state.value.length === 0 ? (
        <Message text={emptyText} />
      ) : (
        renderItems(state.value)
      );
  }
};

const useStableCommunityIds = (communities: Community[]): CommunityId[] => {
  const idsKey = JSON.stringify(communities.map((community) => community.id));
  return useMemo(
    () => communities.map((community) => community.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey],
  );
};

const ModerationPanelContent = ({
  communities,
}: {
  communities: Community[];
}) => {
  const communityIds = useStableCommunityIds(communities);
  const moderation = useCommunityModeration(communityIds);

  return (
    <div className="flex flex-col gap-4">
      <ModerationToggle mode={moderation.mode} onChange={moderation.setMode} />

      {moderation.mode === 'reported'
        ? renderList(moderation.reported, 'No reported posts.', (posts) => (
            <div className="flex flex-col gap-3">
              {posts.map((post) => (
                <ReportedPostCard
                  key={postKey(post)}
                  post={post}
                  busy={moderation.isBusy(postKey(post))}
                  authorBlocked={post.isAuthorBlocked}
                  onRemove={() => void moderation.remove(post)}
                  onBlock={() => void moderation.blockAuthor(post)}
                  onApprove={() => void moderation.approve(post)}
                />
              ))}
            </div>
          ))
        : renderList(moderation.blocked, 'No blocked users.', (users) => (
            <div className="flex flex-col">
              {users.map((user) => (
                <BlockedUserCard
                  key={userKey(user)}
                  user={user}
                  busy={moderation.isBusy(userKey(user))}
                  onUnblock={() => void moderation.unblock(user)}
                />
              ))}
            </div>
          ))}
    </div>
  );
};

export const CommunityModerationPanel = ({
  communities,
  isOpen,
  onClose,
}: Props) => (
  <SideModal
    isOpen={isOpen}
    onClose={onClose}
    title="Moderation"
    description="Keep your community safe"
    testId="moderation-panel"
  >
    {isOpen ? <ModerationPanelContent communities={communities} /> : null}
  </SideModal>
);
